import { Route, Routes, useLocation } from "react-router-dom";
import { protectedRoutes, publicRoutes } from "./routes/RouteConfig";
import { loginSelector, setUserInfo } from "./store/features/loginSlice";
import ProtectedRouteGuard from "./routes/ProtectedRouteGuard";
import PublicRouteGuard from "./routes/PublicRouteGuard";
import PageNotFoundPage from "./pages/public/PageNotFound";
import { useEffect, useState } from "react";
import { checkAuth } from "./services/common/user.services";
import { toastMessage } from "./utils/helper/myToast";
import useOnlineStatus from "./hooks/useOnlineStatus";
import { setOnlineStatus } from "./store/features/settingSlice";
import { loginSuccessAction } from "./store/actions/login.actions";
import { useAppDispatch } from "./store/store";
import { pageEventSelector, setActiveSideBarKey, setPageEvent, setRouteSegment } from "./store/features/dashboardSlice";
import Loader from "./common/ui/Loader";
import { useSelector } from "react-redux";
import { loadAndResubscribe, subscribeFirebase, unsubscribeFirebase } from "./hooks/firebaseSubscribe";
import useFireBaseRTDB from "./hooks/useFireBaseRTDB";

const App = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const pageEvent = useSelector(pageEventSelector);
  const { currentUser } = useSelector(loginSelector);
  useFireBaseRTDB();

  const handleNetworkStatus = (online: boolean) => dispatch(setOnlineStatus(online));
  useOnlineStatus(handleNetworkStatus);
  const initializeAuth = async () => {
    try {
      const res: any = await checkAuth();
      if (res.status === 200) {
        const { data } = res.data;
        dispatch(loginSuccessAction(data));
        return;
      } else {
        dispatch(setUserInfo({ user: null, orgs: null }));
        const { message, isError, showMessage } = res?.response?.data || {};
        if (message) {
          return showMessage && toastMessage(message, isError);
        }
        if (res.message) {
          return toastMessage(res.message, true);
        }
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    if (pathSegments.length > 0) {
      if (["/home/profile", "/home/security"].includes(location.pathname)) {
        dispatch(setRouteSegment(pathSegments));
        dispatch(setActiveSideBarKey(`/${pathSegments[0]}`));
      } else {
        dispatch(setRouteSegment(pathSegments));
        dispatch(setActiveSideBarKey(`/${pathSegments.slice(0, 2).join("/")}`));
      }
    }
  }, [location]);

  useEffect(() => {
    const refreshAuth = async () => {
      if (pageEvent?.keys.includes("documents") && pageEvent?.type === "refresh") {
        const res: any = await checkAuth();
        if (res.status === 200) {
          const { data } = res.data;
          dispatch(loginSuccessAction(data));
        }
      }
    };
    if (pageEvent?.keys.includes("documents") && pageEvent?.type === "refresh") {
      refreshAuth();
    }
  }, [pageEvent]);

  useEffect(() => {
    loadAndResubscribe((path) => {
      subscribeFirebase(path, (data, error) => {
        // console.log(`Subscribed to ${path}`, { data, error });
        if (error) {
          // handle error
          console.log("Error fetching data:", error);
        } else {
          // use data
          // console.log("Fetched data:", data);
          if (data?.length > 0) {
            dispatch(setPageEvent({ keys: ["documents"], type: "refresh" }));
            setTimeout(() => {
              dispatch(setPageEvent(null));
            }, 5000);
            unsubscribeFirebase(path);
          }
        }
      });
    });
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "userLogout") {
        initializeAuth();
      }
      if (event.key === "userLogin") {
        initializeAuth();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  if (isLoading) return <Loader />;
  return (
    <Routes>
      {publicRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={<PublicRouteGuard>{route.element}</PublicRouteGuard>} />
      ))}
      {protectedRoutes.map((route) => {
        const { path, userTypes, Layout, element, notRoles } = route;
        return (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRouteGuard allowedUserTypes={userTypes} permId={route.permId} perms={currentUser?.perms} notRoles={notRoles}>
                <Layout>{element}</Layout>
              </ProtectedRouteGuard>
            }
          />
        );
      })}
      <Route path="*" element={<PageNotFoundPage />} />
    </Routes>
  );
};

export default App;
